import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Task } from '../../../types';
import { TaskFormWrapper } from './TaskFormWrapper';

interface InitialClientContactFormProps {
  task: Task;
  onSubmit: (formData: any) => void;
}

export const InitialClientContactForm: React.FC<InitialClientContactFormProps> = ({
  task,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState({
    clientName: '',
    contactDate: dayjs(),
    contactMethod: '',
    phoneNumber: '',
    email: '',
    incidentDate: dayjs(),
    practiceArea: '',
    briefDescription: '',
    urgentMatter: false,
    referralSource: '',
    followUpScheduled: false,
    followUpDate: dayjs(),
    followUpNotes: '',
    initialImpression: '',
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <TaskFormWrapper task={task} onSubmit={() => onSubmit(formData)}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Client Name"
            fullWidth
            required
            value={formData.clientName}
            onChange={(e) => handleChange('clientName', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Contact Date"
            value={formData.contactDate}
            onChange={(date) => handleChange('contactDate', date)}
            slotProps={{
              textField: { fullWidth: true, required: true }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Contact Method</InputLabel>
            <Select
              value={formData.contactMethod}
              label="Contact Method"
              onChange={(e) => handleChange('contactMethod', e.target.value)}
            >
              <MenuItem value="phone">Phone</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="walk-in">Walk-in</MenuItem>
              <MenuItem value="referral">Referral Contact</MenuItem>
              <MenuItem value="website">Website Inquiry</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone Number"
            fullWidth
            value={formData.phoneNumber}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Email Address"
            fullWidth
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Incident Date"
            value={formData.incidentDate}
            onChange={(date) => handleChange('incidentDate', date)}
            slotProps={{
              textField: { fullWidth: true }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Practice Area</InputLabel>
            <Select
              value={formData.practiceArea}
              label="Practice Area"
              onChange={(e) => handleChange('practiceArea', e.target.value)}
            >
              <MenuItem value="personal-injury">Personal Injury</MenuItem>
              <MenuItem value="family-law">Family Law</MenuItem>
              <MenuItem value="criminal">Criminal Defense</MenuItem>
              <MenuItem value="real-estate">Real Estate</MenuItem>
              <MenuItem value="corporate">Corporate Law</MenuItem>
              <MenuItem value="employment">Employment Law</MenuItem>
              <MenuItem value="estate-planning">Estate Planning</MenuItem>
              <MenuItem value="immigration">Immigration</MenuItem>
              <MenuItem value="intellectual-property">Intellectual Property</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Referral Source"
            fullWidth
            value={formData.referralSource}
            onChange={(e) => handleChange('referralSource', e.target.value)}
            placeholder="How did the client hear about us?"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Brief Description"
            fullWidth
            multiline
            rows={3}
            required
            value={formData.briefDescription}
            onChange={(e) => handleChange('briefDescription', e.target.value)}
            placeholder="Brief description of the potential case"
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.urgentMatter}
                  onChange={(e) => handleChange('urgentMatter', e.target.checked)}
                />
              }
              label="Urgent Matter"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.followUpScheduled}
                  onChange={(e) => handleChange('followUpScheduled', e.target.checked)}
                />
              }
              label="Follow-up Scheduled"
            />
          </Box>
        </Grid>

        {formData.followUpScheduled && (
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Follow-up Date"
              value={formData.followUpDate}
              onChange={(date) => handleChange('followUpDate', date)}
              slotProps={{
                textField: { fullWidth: true }
              }}
            />
          </Grid>
        )}

        {formData.followUpScheduled && (
          <Grid item xs={12}>
            <TextField
              label="Follow-up Notes"
              fullWidth
              multiline
              rows={2}
              value={formData.followUpNotes}
              onChange={(e) => handleChange('followUpNotes', e.target.value)}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            label="Initial Impression"
            fullWidth
            multiline
            rows={3}
            value={formData.initialImpression}
            onChange={(e) => handleChange('initialImpression', e.target.value)}
            placeholder="Initial thoughts on case viability, potential challenges, etc."
          />
        </Grid>
      </Grid>
    </TaskFormWrapper>
  );
};